import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import Lucide from '@/components/Base/Lucide';
import { getApiOrigin } from '@/config/env';
import { setToken } from '@/utils/auth';

function Login() {
  const navigate = useNavigate();
  const [remember, setRemember] = useState(false);
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('remember_id');
    const savedPass = localStorage.getItem('remember_pw');
    if (savedUser) {
      setUsernameOrEmail(savedUser);
      setRemember(true);
    }
    if (savedPass) {
      setPassword(savedPass);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const apiUrl = getApiOrigin();
      const res = await fetch(`${apiUrl}/auth/login-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernameOrEmail, password }),
      });

      const data = (await res.json().catch(() => ({}))) as {
        token?: string;
        message?: string;
        error?: string;
        user?: { role?: string };
      };

      if (!res.ok) {
        setError(data.message || data.error || 'Invalid credentials');
        return;
      }

      if (!data.token) {
        setError('Invalid credentials');
        return;
      }

      setToken(data.token);
      const role = data.user?.role;
      if (role) {
        localStorage.setItem('role', role);
      }

      if (remember) {
        localStorage.setItem('remember_id', usernameOrEmail);
        localStorage.setItem('remember_pw', password);
      } else {
        localStorage.removeItem('remember_id');
        localStorage.removeItem('remember_pw');
      }

      navigate('/', { replace: true });
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again later');
    }
  };

  return (
    <>
      <div className="relative z-50 container grid lg:h-screen grid-cols-12 lg:max-w-[1550px] 2xl:max-w-[1750px] py-10 px-5 sm:py-14 sm:px-10 md:px-36 lg:py-0 lg:pl-14 lg:pr-12 xl:px-24">
        <div className="relative z-50 col-span-12 h-full rounded-2xl bg-white p-7 before:absolute before:inset-0 before:-mb-3.5 before:mx-5 before:rounded-2xl before:bg-white/40 before:content-[''] sm:p-14 lg:col-span-5 lg:bg-transparent lg:p-0 lg:pr-10 xl:pr-24 2xl:col-span-4 dark:bg-darkmode-600 dark:before:hidden">
          <div className="relative z-10 flex h-full w-full flex-col justify-center py-2 lg:py-32">
            <div className="flex h-[55px] w-[55px] items-center justify-center rounded-[0.8rem] border border-primary/30">
              <div className="relative flex h-[50px] w-[50px] items-center justify-center rounded-[0.6rem] bg-gradient-to-b from-theme-1/90 to-theme-2/90 bg-white">
                <div className="relative h-[26px] w-[26px] -rotate-45 [&_div]:bg-white">
                  <div className="absolute inset-y-0 left-0 my-auto h-[75%] w-[20%] rounded-full opacity-50"></div>
                  <div className="absolute inset-0 m-auto h-[120%] w-[20%] rounded-full"></div>
                  <div className="absolute inset-y-0 right-0 my-auto h-[75%] w-[20%] rounded-full opacity-50"></div>
                </div>
              </div>
            </div>
            <div className="mt-10">
              <div className="text-2xl font-medium">Sign In</div>
              <div className="mt-2.5 text-slate-600 dark:text-slate-400">LD Survey Admin Portal</div>
              {showWelcome && (
                <div className="relative my-7 flex items-center rounded-[0.6rem] border border-primary/20 bg-primary/5 px-4 py-3 leading-[1.7]">
                  <Lucide
                    icon="Lightbulb"
                    className="mr-2 h-7 w-7 fill-primary/10 stroke-[0.8]"
                  />
                  <div className="ml-1 mr-8">
                    Welcome to <span className="font-medium">LD Survey</span>. Enter your credentials
                    and click <span className="font-medium">Sign In</span> to continue.
                  </div>
                  <button
                    type="button"
                    className="absolute right-0 my-auto mr-2 px-3 py-2 text-primary"
                    onClick={() => setShowWelcome(false)}
                    aria-label="Close"
                  >
                    <Lucide icon="X" className="h-5 w-5" />
                  </button>
                </div>
              )}
              <div className="mt-6">
                <form onSubmit={handleSubmit}>
                  <label className="mb-2 inline-block">Username*</label>
                  <input
                    type="text"
                    placeholder="e.g. cd-admin"
                    autoComplete="username"
                    value={usernameOrEmail}
                    onChange={(e) => setUsernameOrEmail(e.target.value)}
                    required
                    className="block w-full rounded-[0.6rem] border-slate-300/80 px-4 py-3.5 text-sm shadow-sm transition duration-200 ease-in-out placeholder:text-slate-400/90 focus:border-primary focus:border-opacity-40 focus:ring-4 focus:ring-primary focus:ring-opacity-20"
                  />

                  <label className="mb-2 mt-4 inline-block">Password*</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="************"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="block w-full rounded-[0.6rem] border-slate-300/80 px-4 py-3.5 pr-12 text-sm shadow-sm transition duration-200 ease-in-out placeholder:text-slate-400/90 focus:border-primary focus:border-opacity-40 focus:ring-4 focus:ring-primary focus:ring-opacity-20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                    </button>
                  </div>

                  <div className="mt-4 flex text-xs text-slate-500 sm:text-sm">
                    <div className="mr-auto flex items-center">
                      <input
                        type="checkbox"
                        id="remember-me"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="mr-2.5 cursor-pointer rounded border border-slate-200 shadow-sm transition-all duration-100 ease-in-out focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:ring-offset-0"
                      />
                      <label className="cursor-pointer select-none" htmlFor="remember-me">
                        Remember me
                      </label>
                    </div>
                    <button
                      type="button"
                      className="text-primary"
                      onClick={() => alert('TODO: implement Forgot Password')}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  {error && (
                    <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-600">
                      {error}
                    </div>
                  )}

                  <div className="mt-5 text-center xl:mt-8 xl:text-left">
                    <button
                      type="submit"
                      className="inline-flex w-full cursor-pointer items-center justify-center rounded-full border border-primary bg-primary px-3 py-3.5 font-medium text-white shadow-sm transition duration-200 focus:ring-4 focus:ring-primary focus:ring-opacity-20"
                    >
                      Sign In
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="fixed inset-0 z-0 container grid h-screen w-screen grid-cols-12 pl-14 pr-12 lg:max-w-[1550px] 2xl:max-w-[1750px] xl:px-24">
        <div className="relative z-20 col-span-12 h-screen after:absolute after:inset-y-0 after:right-0 after:hidden after:w-[800%] after:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] after:bg-gradient-to-b after:from-white after:to-slate-100/80 after:content-[''] lg:col-span-5 lg:after:block 2xl:col-span-4 dark:after:bg-darkmode-600 dark:after:from-darkmode-600 dark:after:to-darkmode-600 before:absolute before:inset-y-0 before:right-0 before:-mr-4 before:my-6 before:hidden before:w-[800%] before:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] before:bg-white/50 before:bg-gradient-to-b before:from-white/10 before:to-slate-50/10 before:content-[''] lg:before:block dark:before:from-darkmode-300 dark:before:to-darkmode-300"></div>
        <div className="col-span-7 h-full before:absolute before:inset-y-0 before:left-0 before:w-screen before:bg-gradient-to-b before:from-theme-1 before:to-theme-2 before:content-[''] after:absolute after:inset-y-0 after:left-0 after:w-screen after:bg-fixed after:bg-center after:bg-no-repeat after:content-[''] after:bg-texture-white lg:relative lg:before:w-[800%] lg:after:w-[800%] lg:after:bg-[25rem_-25rem] 2xl:col-span-8">
          <div className="sticky top-0 z-10 ml-16 hidden h-screen flex-col justify-center lg:flex xl:ml-28 2xl:ml-36">
            <div className="text-[2.6rem] font-medium leading-[1.4] text-white xl:text-5xl xl:leading-[1.2]">
              LD Survey <br /> Admin Portal
            </div>
            <div className="mt-5 text-base leading-relaxed text-white/70 xl:text-lg">
              Manage survey operations, staff ambassadors, and campaign data from one place.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
